/**
 * 進み具合を円形のゲージで示す。**一覧の行に収まる大きさ**に作ってある。
 *
 * 一覧で知りたいのは「どの見積がどこまで進んでいるか」で、行を流し見したときに
 * 円の欠け方で分かるのが一番速い。数字だけだと 2 つ並べて比べないと分からない。
 *
 * 分子・分母を円の右に小さく出す。割合だけだと「3/4」と「30/40」の区別が付かず、
 * 残りの手間が読めない。
 *
 * SVG で描いているのは、HeroUI の CircularProgress が中に文字を置けず、行の高さ
 * （48px）に収める指定も持たないため。
 */
export default function ProgressGauge({
  value,
  total,
  size = 36,
  label,
}: {
  /** 分子（済んだ数）。 */
  value: number;
  /** 分母（全体の数）。0 なら「—」を出す。 */
  total: number;
  /** 円の直径（px）。 */
  size?: number;
  /** 読み上げ用の説明。 */
  label?: string;
}) {
  if (total <= 0) {
    return <span className="text-default-300">—</span>;
  }

  const ratio = Math.min(Math.max(value / total, 0), 1);
  const percent = Math.round(ratio * 100);
  // 線幅は直径に対する比で決める。大きさを変えても見た目の太さが揃う。
  const stroke = Math.max(3, Math.round(size * 0.11));
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;

  return (
    <div
      className="flex items-center gap-1.5"
      role="img"
      aria-label={label ?? `${total} 件中 ${value} 件（${percent}%）`}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden>
        {/* 12 時の位置から時計回りに増やす（左に 90 度回す）。 */}
        <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={stroke}
            className="text-default-200"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={circumference * (1 - ratio)}
            // 全部済んだら緑。一覧を流し見して「終わった見積」が分かるようにする。
            className={percent === 100 ? "text-success-500" : "text-brand-500"}
          />
        </g>
        <text
          x="50%"
          y="50%"
          textAnchor="middle"
          dominantBaseline="central"
          // 「100%」は 1 文字多いので少し小さくする。同じ大きさだと輪に触れる。
          fontSize={size * (percent === 100 ? 0.25 : 0.28)}
          className="fill-default-600 font-bold"
        >
          {percent}%
        </text>
      </svg>
      {/* 分数。横書き（3/4）にすると行の幅を取るので縦に積む。 */}
      <span className="flex flex-col items-center leading-none text-default-500">
        <span className="text-[10px]">{value}</span>
        <span className="my-[2px] h-px w-3.5 bg-default-400" />
        <span className="text-[10px]">{total}</span>
      </span>
    </div>
  );
}
